import React, { PropsWithChildren } from 'react'

import { LoadingSpinner } from 'src/components/atoms/LoadingSpinner'
import { VideoPlayer } from 'src/components/atoms/VideoPlayer'
import { ActionBlockRebrand } from 'src/components/molecules/ActionBlockRebrand'
import { CalculatorSectionRebrand } from 'src/components/molecules/CalculatorSectionRebrand'
import { CtaBandSectionRebrand } from 'src/components/molecules/CtaBandSectionRebrand'
import { FaqSectionRebrand } from 'src/components/molecules/FaqSectionRebrand'
import { HeadingTextSectionRebrand } from 'src/components/molecules/HeadingTextSectionRebrand'
import { HubspotFormRebrand } from 'src/components/molecules/HubspotFormRebrand'
import { ImageHeadingTextSectionRebrand } from 'src/components/molecules/ImageHeadingTextSectionRebrand'
import { ImageListSectionRebrand } from 'src/components/molecules/ImageListSectionRebrand'
import { ImageTextSectionRebrand } from 'src/components/molecules/ImageTextSectionRebrand'
import { PackagesSectionRebrand } from 'src/components/molecules/PackagesSectionRebrand'
import { SplitTextSectionRebrand } from 'src/components/molecules/SplitTextSectionRebrand'
import { TilesSectionRebrand } from 'src/components/molecules/TilesSectionRebrand'
import { WidgetSectionRebrand } from 'src/components/molecules/WidgetSectionRebrand'
import { Content } from 'src/components/molecules/sanity/Content'
import { SanityHeroRebrand } from 'src/components/molecules/sanity/SanityHeroRebrand'
import { ActivitySliderRebrand } from 'src/components/organisms/ActivitySliderRebrand'
import { ArticleCarouselRebrand } from 'src/components/organisms/ArticleCarouselRebrand'
import { CarbonCalculatorSectionRebrand } from 'src/components/organisms/CarbonCalculatorSectionRebrand'
import { CarbonScenariosSectionRebrand } from 'src/components/organisms/CarbonScenariosSectionRebrand'
import { FeaturedPostsSectionRebrand } from 'src/components/organisms/FeaturedPostsSectionRebrand'
import { FeaturedProjectsSectionRebrand } from 'src/components/organisms/FeaturedProjectsSectionRebrand'
import { FunderLeaderboardSectionRebrand } from 'src/components/organisms/FunderLeaderboardSectionRebrand'
import { HubspotContactForm } from 'src/components/organisms/HubspotContactForm'
import { ImageTilesSectionRebrand } from 'src/components/organisms/ImageTilesSectionRebrand'
import { LatestPostSectionRebrand } from 'src/components/organisms/LatestPostSectionRebrand'
import { OurTeamRebrand } from 'src/components/organisms/OurTeamRebrand'
import { PlantingProjectsSectionRebrand } from 'src/components/organisms/PlantingProjectsSectionRebrand'
import { PostsListingSectionRebrand } from 'src/components/organisms/PostsListingSectionRebrand'
import { ProjectCarouselRebrand } from 'src/components/organisms/ProjectCarouselRebrand'
import { ProjectDetailSectionRebrand } from 'src/components/organisms/ProjectDetailSectionRebrand'
import { QuoteSectionRebrand } from 'src/components/organisms/QuoteSectionRebrand'
import { RegistriesSectionRebrand } from 'src/components/organisms/RegistriesSectionRebrand'
import { RegistryDetailSectionRebrand } from 'src/components/organisms/RegistryDetailSectionRebrand'

import { NotFound } from 'src/pages/404/404'
import { NavigationContext } from 'src/types/account'
import { ArrayElement } from 'src/types/common'
import { getSanityImageUrl } from 'src/utils/sanity'
import { trpc } from 'src/utils/trpc'

import { Page, PageBackgroundColor, PageProps } from '..'
import {
  FullWidthContainer,
  FullWidthContainerBackgroundColor
} from '../../Container'
import { SectionSpacerRebrand } from './SectionSpacerRebrand'
import { SanityContentPageResult } from './types'

type SanityContentPageProps = Omit<PageProps, 'children' | 'title'> &
  PropsWithChildren<{
    slug: string
    title?: string
  }>

type SectionContentData = ArrayElement<
  NonNullable<SanityContentPageResult['content']>
>

const getContentSection = (data: SectionContentData): JSX.Element | null => {
  switch (data._type) {
    case 'content':
      return <Content {...data} />
    case 'imageTilesRebrand':
      return <ImageTilesSectionRebrand {...data} />
    case 'spacerRebrand':
      return <SectionSpacerRebrand {...data} />
    case 'ctaBandRebrand':
      return <CtaBandSectionRebrand {...data} />
    case 'tilesRebrand':
      return <TilesSectionRebrand {...data} />
    case 'imageListRebrand':
      return (
        <ImageListSectionRebrand
          {...data}
          imagePosition={data.direction === 'imageRight' ? 'right' : 'left'}
        />
      )
    case 'imageTextRebrand':
      return (
        <ImageTextSectionRebrand
          {...data}
          imagePosition={data.direction === 'imageRight' ? 'right' : 'left'}
        />
      )
    case 'headingTextRebrand':
      return <HeadingTextSectionRebrand {...data} />
    case 'actionBlockRebrand':
      return <ActionBlockRebrand {...data} />
    case 'featuredProjectsRebrand':
      return <FeaturedProjectsSectionRebrand {...data} />
    case 'featuredPostsRebrand':
      return <FeaturedPostsSectionRebrand {...data} />
    case 'funderLeaderboardRebrand':
      return <FunderLeaderboardSectionRebrand />
    case 'imageHeadingTextRebrand':
      return <ImageHeadingTextSectionRebrand {...data} />
    case 'plantingProjectsRebrand':
      return <PlantingProjectsSectionRebrand />
    case 'registriesRebrand':
      return <RegistriesSectionRebrand />
    case 'carbonCalculatorRebrand':
      return <CarbonCalculatorSectionRebrand />
    case 'carbonScenariosRebrand':
      return <CarbonScenariosSectionRebrand />
    case 'projectDetailRebrand':
      return <ProjectDetailSectionRebrand />
    case 'registryDetailRebrand':
      return <RegistryDetailSectionRebrand />
    case 'widgetSectionRebrand':
      return <WidgetSectionRebrand {...data} />
    case 'faqSectionRebrand':
      return <FaqSectionRebrand {...data} />
    case 'contactForm':
      return <HubspotContactForm {...data} />
    case 'hubspotFormRebrand':
      return <HubspotFormRebrand {...data} />
    case 'ourTeamRebrand':
      return <OurTeamRebrand {...data} />
    case 'quoteRebrand':
      return <QuoteSectionRebrand {...data} />
    case 'splitTextRebrand':
      return <SplitTextSectionRebrand {...data} />
    case 'activitySliderRebrand':
      return <ActivitySliderRebrand {...data} />
    case 'calculatorSectionRebrand':
      return <CalculatorSectionRebrand {...data} />
    case 'latestPostRebrand':
      return <LatestPostSectionRebrand {...data} />
    case 'postsListingRebrand':
      return <PostsListingSectionRebrand {...data} />
    case 'packagesSectionRebrand':
      return <PackagesSectionRebrand {...data} />
    case 'video':
      return <VideoPlayer {...data} />
    case 'projectCarousel':
      return <ProjectCarouselRebrand {...data} />
    case 'articleCarousel':
      return <ArticleCarouselRebrand {...data} />
    default:
      return null
  }
}

const FULL_BLEED_TYPES: SectionContentData['_type'][] = [
  'imageTextRebrand',
  'imageHeadingTextRebrand',
  'headingTextRebrand',
  'imageListRebrand',
  'featuredProjectsRebrand',
  'featuredPostsRebrand',
  'funderLeaderboardRebrand',
  'plantingProjectsRebrand',
  'registriesRebrand',
  'carbonCalculatorRebrand',
  'carbonScenariosRebrand',
  'projectDetailRebrand',
  'registryDetailRebrand',
  'actionBlockRebrand',
  'ctaBandRebrand',
  'quoteRebrand',
  'splitTextRebrand',
  'activitySliderRebrand',
  'faqSectionRebrand',
  'hubspotFormRebrand',
  'calculatorSectionRebrand',
  'latestPostRebrand',
  'postsListingRebrand',
  'tilesRebrand',
  'imageTilesRebrand',
  'ourTeamRebrand',
  'calculatorSectionRebrand',
  'packagesSectionRebrand',
  'widgetSectionRebrand',
  'projectCarousel',
  'articleCarousel'
]

const renderContentSection = (
  sectionData: SectionContentData,
  index: number
) => {
  const component = getContentSection(sectionData)

  if (!component) return null

  const isFullBleed = FULL_BLEED_TYPES.includes(sectionData._type)

  return (
    <FullWidthContainer
      key={`container_${index}`}
      skipContainer={isFullBleed}
      className='py-0!'
      backgroundColor={FullWidthContainerBackgroundColor.transparent}
    >
      {component}
    </FullWidthContainer>
  )
}

const SanityContentPage = ({
  slug,
  backgroundColor = PageBackgroundColor.linen,
  ...pageProps
}: SanityContentPageProps) => {
  const { data, isLoading } = trpc.useQuery([
    'sanity.getContentPageBySlug',
    { slug }
  ])

  if (!isLoading && !data) return <NotFound />

  return (
    <Page
      title={data?.title || 'Untitled page'}
      navigationContext={NavigationContext.Public}
      isFullWidth
      metaTitle={data?.metaTitle || data?.title}
      metaDescription={data?.metaDescription || data?.description}
      image={data?.openGraphImage && getSanityImageUrl(data.openGraphImage)}
      showHero={false}
      backgroundColor={backgroundColor}
      {...pageProps}
    >
      <LoadingSpinner isLoading={isLoading}>
        {data?.heroRebrand && <SanityHeroRebrand {...data.heroRebrand} />}
        <div className='flex flex-col mx-auto'>
          {data?.content?.map(renderContentSection)}
        </div>
      </LoadingSpinner>
    </Page>
  )
}

export default SanityContentPage
